import React, { lazy, Suspense, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { FullPageSpinner } from 'src/components/Reusable/FullPageSpinner'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import 'bootstrap/dist/css/bootstrap.min.css'
import 'react-toastify/dist/ReactToastify.css'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useAuth } from '../context/authContext'
import { AppUnauth } from './AppUnauth'
import { usePublicTrustPortalAccount } from './App.hooks'
import { PublicTrustAccountProvider } from './TrustApp/context/PublicTrustAppContext'

type ErrorProps = {
  error: Error
}

const ErrorFallback = (props: ErrorProps): JSX.Element => {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{props.error.message}</pre>
    </div>
  )
}

export const App = (): JSX.Element => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ProductChooser />
    </ErrorBoundary>
  )
}

const ProductChooser = () => {
  const isTrustPortalEnabled = IsGatekeeperEnabled(GatekeeperFlag.TRUST_PORTAL)
  const { trustAccountId, trustPath } = usePublicTrustPortalAccount()

  if (isTrustPortalEnabled) {
    // Tri-state logic:
    // 1. Loading - fetching trust account id
    // 2. No account id - redirect to main app, we are not in the trust portal
    // 3. Account id - trust account id found, render trust portal
    if (trustAccountId === undefined) {
      return <FullPageSpinner />
    }

    if (trustAccountId) {
      return (
        <TrustPortal trustAccountId={trustAccountId} trustPath={trustPath} />
      )
    }
  }

  return <AppChooser />
}

const TrustPortal = ({
  trustAccountId,
  trustPath,
}: {
  trustAccountId: string
  trustPath?: string
}) => {
  const TrustApp = useMemo(() => lazy(() => import('./TrustApp')), [])

  return (
    <Suspense fallback={<></>}>
      <PublicTrustAccountProvider
        trustAccountId={trustAccountId}
        trustPath={trustPath}
      >
        <TrustApp />
      </PublicTrustAccountProvider>
    </Suspense>
  )
}

const AppChooser = () => {
  const AppAuth = useMemo(() => lazy(() => import('./AppAuth')), [])

  const { accountId, isAuthenticated } = useAuth()
  if (!isAuthenticated || !accountId) {
    return <AppUnauth />
  }

  return (
    <Suspense fallback={<></>}>
      <AppAuth accountId={accountId} />
    </Suspense>
  )
}
